import React, { useMemo, useState } from 'react';
import Button from "@mui/material/Button";

// Variante de DefaultChoiceState pour les "associations":
// - 1er niveau: choix d'une classe d'action (action.class)
// - 2ème niveau: liste des actions de cette classe + bouton "Retour"
const AssociationChoiceState = ({ state, userId, setConfirmAction }) => {
    // selectedClass stores the GROUP_DEFS key (XXX), not the full action.class (action.XXX)
    const [selectedClass, setSelectedClass] = useState(null);

    // Classes "grosses" qu'on souhaite regrouper, avec titre + image
    const GROUP_DEFS = {
        ActionSelectUniversity: { title: "Université", image: "/public/arknova/actions/university_empty.png" },
        ActionSelectZooPartner: { title: "Zoo Partenaire", image: "/public/arknova/actions/zoo_partner_empty.png" },
        ActionSupportConservation: { title: "Projet de Conservation", image: "/public/arknova/actions/support_project.png" }
    };

    const defaultStyleButton = {
        width: '100%',
        backgroundColor: "#2a2d4a",
        borderRadius: "8px",
        display: "flex",
        marginBottom: "8px",
        padding: "10px 15px",
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        transition: 'all 0.2s ease',
        boxSizing: 'border-box',
        minHeight: "8vh"
    };

    const bonusActions = ["money", "appeal", "reputation", "conservation_point", "x_token", "posturing", "digging", "hunter", "scavenger", "pouch", "perception", "sunbathing"];

    const actions = state?.actions?.[userId] ?? [];

    // Ordre voulu:
    // 1) ActionAssociationReputation
    // 2) Groupes: Zoo Partner > Université > Projet
    // 3) Toutes les autres actions
    // 4) ActionGoBack toujours en dernier
    const GROUP_ORDER = [
        "ActionSelectZooPartner",
        "ActionSelectUniversity",
        "ActionSupportConservation"
    ];

    const ACTION_FIRST = "ActionAssociationReputation";
    const ACTION_LAST = "ActionGoBack";

    const normalizeActionKey = (a) => {
        const rawClass = a?.class ?? a?.actionClass ?? a?.type;
        return typeof rawClass === 'string' && rawClass.startsWith('action.') ? rawClass.slice('action.'.length) : rawClass;
    };

    // Regroupement uniquement pour les classes listées dans GROUP_DEFS
    // Le backend envoie action.class sous la forme "action.XXX"
    const { groupedActions, ungroupedActions } = useMemo(() => {
        const grouped = new Map(); // key: XXX
        const ungrouped = [];

        for (const a of actions) {
            const key = normalizeActionKey(a);

            if (key && GROUP_DEFS[key]) {
                if (!grouped.has(key)) grouped.set(key, []);
                grouped.get(key).push(a);
            } else {
                ungrouped.push(a);
            }
        }
        return { groupedActions: grouped, ungroupedActions: ungrouped };
    }, [actions]);

    const sortedGroupClasses = useMemo(() => {
        const present = Array.from(groupedActions.keys());
        const inOrder = GROUP_ORDER.filter(k => present.includes(k));
        const rest = present.filter(k => !GROUP_ORDER.includes(k)).sort((a, b) => String(a).localeCompare(String(b), 'fr'));
        return [...inOrder, ...rest];
    }, [groupedActions]);

    const sortedUngroupedActions = useMemo(() => {
        const first = [];
        const middle = [];
        const last = [];

        for (const a of ungroupedActions) {
            const key = normalizeActionKey(a);
            if (key === ACTION_LAST) last.push(a);
            else if (key === ACTION_FIRST) first.push(a);
            else middle.push(a);
        }

        // Tri stable/agréable sur le libellé pour le "milieu"
        middle.sort((a, b) => String(a?.name ?? '').localeCompare(String(b?.name ?? ''), 'fr'));

        return { first, middle, last };
    }, [ungroupedActions]);

    const getSortedActionsForGroup = (groupKey) => {
        const list = groupedActions.get(groupKey) ?? [];
        const last = [];
        const rest = [];
        for (const a of list) {
            const key = normalizeActionKey(a);
            if (key === ACTION_LAST) last.push(a);
            else rest.push(a);
        }
        rest.sort((a, b) => String(a?.name ?? '').localeCompare(String(b?.name ?? ''), 'fr'));
        return [...rest, ...last];
    };

    const renderAction = (a) => {
        return (
            <div
                style={defaultStyleButton}
                key={a.name}
            >
                {a.imageName &&
                    !bonusActions.some(bonus => a.imageName.toLowerCase().startsWith("bonus_" + bonus)) && (
                        <img
                            style={{ width: "5vh", marginRight: "12px" }}
                            src={"/public/arknova/actions/" + a.imageName.toLowerCase() + ".png"}
                            alt={" "}
                        />
                    )}

                {bonusActions.map(bonus =>
                    a.imageName && a.imageName.toLowerCase().startsWith("bonus_" + bonus) ? (
                        <div
                            key={bonus}
                            style={{
                                width: "5vh",
                                marginRight: "12px",
                                position: "relative",
                                height: "6vh",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center"
                            }}
                        >
                            <img
                                style={{ width: "100%", height: "auto" }}
                                src={"/public/arknova/actions/bonus_" + (/_\d+$/.test(bonus) ? bonus.replace(/_\d+$/, '') : bonus) + ".png"}
                                alt={" "}
                            />
                            {/_\d+$/.test(bonus) && (
                                <div
                                    style={{
                                        fontSize: "28px",
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "5vh",
                                        height: "6vh",
                                        textShadow: "-1px 0 black, 0 1px black, 1px 0 black, 0 -1px black",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center"
                                    }}
                                >
                                    {a.imageName.toLowerCase().split("_")[a.imageName.split("_").length - 1]}
                                </div>
                            )}
                        </div>
                    ) : null
                )}

                <div style={{ flex: 1, fontSize: "16px", minWidth: 0 }}>
                    {a.name}
                </div>
                <div style={{ marginLeft: "6px" }}>
                    <Button
                        variant={"contained"}
                        disabled={!a.ok}
                        onClick={() => setConfirmAction({ action: a, message: a.name + " ?" })}
                        sx={{ minWidth: '80px' }}
                    >
                        Jouer
                    </Button>
                </div>
            </div>
        );
    };

    const renderClassButton = (cls) => {
        const def = GROUP_DEFS[cls] || { title: String(cls) };
        const actionsInGroup = groupedActions.get(cls) ?? [];
        const okCount = actionsInGroup.reduce((acc, a) => acc + (a?.ok ? 1 : 0), 0);

        return (
            <div
                style={defaultStyleButton}
                key={String(cls)}
            >
                {def.image && (
                    <img
                        style={{ width: "5vh", marginRight: "12px" }}
                        src={def.image}
                        alt={" "}
                    />
                )}

                <div style={{ flex: 1, fontSize: "16px", minWidth: 0 }}>
                    {def.title}
                    <span style={{ opacity: 0.7, marginLeft: '8px', fontSize: '14px' }}>({okCount})</span>
                </div>
                <div style={{ marginLeft: "6px" }}>{
                    <Button
                        variant={"contained"}
                        disabled={okCount === 0}
                        onClick={() => setSelectedClass(cls)}
                        sx={{ minWidth: '110px' }}
                    >
                        Ouvrir
                    </Button>
                }</div>
            </div>
        );
    };

    return (
        <div>
            <h2>
                C'est à vous de jouer !
            </h2>

            {!selectedClass && (
                <div>
                    {/* Action prioritaire */}
                    {sortedUngroupedActions.first.map((a) => renderAction(a))}

                    {/* Groupes (seulement certaines classes) */}
                    {sortedGroupClasses.map((cls) => renderClassButton(cls))}

                    {/* Toutes les autres actions s'affichent directement */}
                    {sortedUngroupedActions.middle.map((a) => renderAction(a))}

                    {/* Retour toujours en dernier */}
                    {sortedUngroupedActions.last.map((a) => renderAction(a))}
                </div>
            )}

            {selectedClass && (
                <div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
                        <Button variant={"outlined"} onClick={() => setSelectedClass(null)}>
                            Retour
                        </Button>
                        <div style={{ fontSize: '16px', opacity: 0.85 }}>
                            {GROUP_DEFS[selectedClass]?.title ?? String(selectedClass)}
                        </div>
                    </div>

                    <div>
                        {getSortedActionsForGroup(selectedClass).map((a) => renderAction(a))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AssociationChoiceState;
export { AssociationChoiceState };
